import { AxiosResponse } from 'axios';

/**
 * This should help to move backend errors to the frontend. Simple.
 */
export enum TranslatedErrorCode {
  // Validations
  INVALID_INVITE_CODE = 'invite_code_not_valid',
  EMAIL_NOT_UNIQUE = 'email_not_unique',
  EMAIL_NOT_VALID = 'email_not_valid',
  EMAIL_NOT_VERIFIED = 'email_not_verified',
  MOBILE_NUMBER_IN_USE = 'mobile_number_in_use',
  FIRSTNAME_NOT_CONSISTENT = 'firstname_not_consistent',
  FIRSTNAME_MIN_LENGTH = 'firstname_min_length',
  FIRSTNAME_MAX_LENGTH = 'firstname_max_length',
  FIRSTNAME_SIZE = 'firstname_size',
  FIRSTNAME_EMPTY = 'firstname_empty',
  LASTNAME_NOT_CONSISTENT = 'lastname_not_consistent',
  LASTNAME_MIN_LENGTH = 'lastname_min_length',
  LASTNAME_MAX_LENGTH = 'lastname_max_length',
  LASTNAME_SIZE = 'lastname_size',
  LASTNAME_EMPTY = 'lastname_empty',
  REGISTRATION_LEGAL_NOT_CONFIRMED = 'registration_legal_not_confirmed',
  MOBILE_NUMBER_NOT_VALID = 'mobile_number_not_valid',
  MOBILE_NUMBER_NOT_VERIFIED = 'mobile_number_not_verified',
  PASSWORD_EMPTY = 'password_empty',
  PASSWORD_TOO_SHORT = 'password_too_short',
  PASSWORD_NOT_VALID = 'password_not_valid',
  USER_ID_NOT_VALID = 'user_id_not_valid',
  // Errors
  UNSPECIFIC_ERROR = 'unspecific_error',
  INVITE_CODE_ALREADY_USED = 'invite_code_already_used',
  REGISTRATION_WRONG_STATUS = 'wrong_status',
  SETTINGS_MAIL_EXISTS = 'mail_exists',
  MOBILE_VERIFICATION_CODE_INVALID = 'mobile_verification_code_invalid',
  EMAIL_CODE_INVALID = 'email_code_not_valid',
  PASSWORD_DOES_NOT_MATCH = 'password_does_not_match',
  TOO_MANY_LOGINS = 'too_many_logins',
  UNKNOWN_ERROR = 'unknown_error',
  LOGIN_NOT_FOUND = 'login_not_found',
  PLEASE_LOGIN = 'please_login',
  ACCOUNT_SUSPENDED = 'account_suspended',
  PASSWORD_ALREADY_GIVEN = 'password_already_given',
  REGISTRATION_INVALID_USERSKILL_ID = 'registration_invalid_userskill_id',
  REGISTRATION_REG_ID_AND_PROC_ID_NOT_FOUND = 'registration_reg_id_and_proc_id_not_found',
  REGISTRATION_MISSING_PARAMETER = 'registration_missing_parameter',
  REGISTRATION_USERSKILL_NOT_FOUND = 'registration_userskill_not_found',
  REGISTRATION_MOBILE_NUMBER_MISSING = 'registration_mobile_number_missing',
  REGISTRATION_SKILLS_POKID_INVALID = 'registration_skills_pokid_invalid',
  REGISTRATION_INVALID_USERSKILLID_OR_USERPOKID = 'registration_invalid_userskillid_or_userpokid',
  REGISTRATION_USERPOK_NOT_FOUND = 'registration_userpok_not_found',
  COMPANY_NAME_ALREADY_EXIST = 'registration_company_name_already_exist',
  OFFER_NOT_FOUND = 'offer_not_found',
  CONTRACT_ONLY_PARTNER_ALLOWED = 'contract_only_partner_allowed',
  INVALID_INPUT_DATA = 'invalid_input_data',
  CONTRACT_PROJECT_REFERENCE_CANNOT_BE_OVERWRITTEN = 'contract_project_reference_cannot_be_overwritten',
  CONTRACT_STATUS_CHANGE_FORBIDDEN = 'contract_status_change_forbidden',
  POSTCODE_NOT_FOUND = 'postcode_not_found',
  INVALID_COUNTRY_ISO_CODE = 'invalid_country_iso_code',
  CONTRACT_DELETE_FORBIDDEN_EXISTING_DOCUMENTS = 'contract_delete_forbidden_existing_documents',
  CONTRACT_DOC_ONLY_CREATOR_ALLOWED = 'contract_doc_only_creator_allowed',
  CONTRACT_DOC_ONLY_PARTNER_ALLOWED = 'contract_doc_only_partner_allowed',
  CONTRACT_DOCUMENT_ALREADY_SIGNED = 'contract_document_already_signed',
  CONTRACT_DOC_ALREADY_VIEWED = 'contract_doc_already_viewed',
  ALREADY_REGISTERED = 'already_registered',
  PROJECT_LOCKED = 'project_locked',
  NOT_PARTNER_OR_ADMIN = 'not_partner_or_admin',
  PROMOTION_ONLY_AVAILABLE_TO_CONTRACTORS = 'promotion_only_available_to_contractors',
  DELETE_ONLY_FOR_DRAFT = 'delete_only_for_draft',
  INVOICE_DATA_INCOMPLETE = 'invoice_data_incomplete',
  INVOICE_SENDER_NOT_PARTNER = 'invoice_sender_not_partner',
  ONLY_CREATOR_CAN_ADD_ATTACHMENT = 'only_creator_can_add_attachment',
  ATTACHMENT_LIMIT_REACHED = 'attachment_limit_reached',
  ATTACHMENT_TOO_LARGE = 'attachment_too_large',
  ATTACHMENT_TOTAL_SIZE_EXCEEDED = 'attachment_total_size_exceeded',
  ATTACHMENT_NOT_FOUND = 'attachment_not_found',
  INVOICE_ALREADY_CANCELLED = 'invoice_already_cancelled',
  ALREADY_PAID = 'already_paid',
  NOT_INVOICE_PARTNER = 'not_invoice_partner',
  MODIFY_ONLY_IN_DRAFT = 'modify_only_in_draft',
  NOT_DRAFT_INVOICE = 'not_draft_invoice',
  ALREADY_EXTENDED = 'already_extended',
  UPDATE_POSITION_NOT_FOUND = 'update_position_not_found',
  MEETING_SELF_INVITE = 'meeting_self_invite',
  MEETING_NOT_FOUND = 'meeting_not_found',
  MEETING_TIMESLOT_INVALID = 'meeting_timeslot_invalid',
  MEETING_INVITATION_NOT_OPEN = 'meeting_invitation_not_open',
  MEETING_ALREADY_CLOSED = 'meeting_already_closed',
  MEETING_DEADLINE_EXPIRED = 'meeting_deadline_expired',
  MEETING_CREATOR_CANNOT_ACCEPT = 'meeting_creator_cannot_accept',
  INVALID_REQUEST_DATA = 'invalid_request_data',
  INVALID_MONTH_VALUE = 'invalid_month_value',
}

export interface ValidationFieldErrorMessage {
  key: string;
  params?: unknown[];
}

export interface ValidationFieldError {
  field: string;
  messages: ValidationFieldErrorMessage[];
}

export interface ParamsErrorDataResponse {
  key: string;
  value: string;
}

export interface ErrorDataResponse {
  code: string;
  params: ParamsErrorDataResponse[];
}

export interface PossibleErrorResponse extends AxiosResponse {
  requestId: string;
  validation?: ValidationFieldError[];
  error?: ErrorDataResponse;
}

export interface BackendError {
  code: TranslatedErrorCode;
  params?: unknown;
  inputField?: string | null;
}

export interface BackendErrors {
  errorCode?: TranslatedErrorCode | string | null;
  validationErrors?: BackendError[] | null;
  code?: string | null;
}
